import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { createActions } from '../../../../store';
import SliderListItem from '../../../../../../list-items/SliderListItem';
import CollapsibleSwitchListItem from '../../../../../../list-items/CollapsibleSwitchListItem';
import CondensedListItem from '../../../../../../list-items/CondensedListItem';
import { DEFAULT_FLOCK_SETTINGS, FLOCK_SETTINGS_RANGES } from '../../../../CreatePageConstants';

// Sliders run 0..UI_MAX, except Speed, which starts at 1 so the flock can never be
// left standing still; the raw values they map onto live in FLOCK_SETTINGS_RANGES.
const UI_MAX = 100;
const AGILITY_UI_MAX = 300;

const toUi = (key, value) => {
    const { min, max } = FLOCK_SETTINGS_RANGES[key];
    return Math.round(((value - min) / (max - min)) * UI_MAX);
};

const toInternal = (key, value) => {
    const { min, max } = FLOCK_SETTINGS_RANGES[key];
    return min + (value / UI_MAX) * (max - min);
};

const ParticlesFlockListItem = (props) => {
    const { separation, alignment, cohesion, vision, speed, turnSpeed, customMode, updateElementsSettings } = props;

    const handlers = useMemo(() => {
        const update = (key) => (value) => updateElementsSettings({ particles: { flock: { [key]: toInternal(key, value) } } });
        return {
            separation: update('separation'),
            alignment: update('alignment'),
            cohesion: update('cohesion'),
            vision: update('vision'),
            speed: update('speed'),
            turnSpeed: update('turnSpeed'),
        };
    }, [updateElementsSettings]);

    const onCustomChange = (value) => {
        if (value) {
            updateElementsSettings({
                particles: { flock: { separation, alignment, cohesion, vision, speed, turnSpeed, customMode: true } }
            });
        } else {
            updateElementsSettings({ particles: { flock: { customMode: false } } });
        }
    };

    return (
        <CollapsibleSwitchListItem
            text='Customize Movement'
            checked={customMode}
            onChange={onCustomChange}
        >
                <CondensedListItem
                    elements={[
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Speed'
                                    value={toUi('speed', speed)}
                                    min={1}
                                    max={100}
                                    step={1}
                                    onValueChange={handlers.speed}
                                />
                            )
                        },
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Separation'
                                    value={toUi('separation', separation)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={handlers.separation}
                                    toolTip={'The distance between particles.'}
                                />
                            )
                        }
                    ]}
                />
                <CondensedListItem
                    elements={[
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Alignment'
                                    value={toUi('alignment', alignment)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={handlers.alignment}
                                    toolTip={'How strongly particles move in the same direction.'}
                                />
                            )
                        },
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Cohesion'
                                    value={toUi('cohesion', cohesion)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={handlers.cohesion}
                                    toolTip={'How strongly particles group together.'}
                                />
                            )
                        }
                    ]}
                />
                <CondensedListItem
                    elements={[
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Vision'
                                    value={toUi('vision', vision)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={handlers.vision}
                                    toolTip={'The distance each particle can see and react to its neighbors from.'}
                                />
                            )
                        },
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Agility'
                                    value={toUi('turnSpeed', turnSpeed)}
                                    min={0}
                                    max={AGILITY_UI_MAX}
                                    step={1}
                                    onValueChange={handlers.turnSpeed}
                                    toolTip={'How quickly particles can change direction.'}
                                />
                            )
                        }
                    ]}
                />
        </CollapsibleSwitchListItem>
    );
};

const mapStateToProps = (state) => {
    const { flock } = state.createPreview.present.videoSettings.elements.particles;
    return {
        separation: flock?.separation ?? DEFAULT_FLOCK_SETTINGS.separation,
        alignment: flock?.alignment ?? DEFAULT_FLOCK_SETTINGS.alignment,
        cohesion: flock?.cohesion ?? DEFAULT_FLOCK_SETTINGS.cohesion,
        vision: flock?.vision ?? DEFAULT_FLOCK_SETTINGS.vision,
        speed: flock?.speed ?? DEFAULT_FLOCK_SETTINGS.speed,
        turnSpeed: flock?.turnSpeed ?? DEFAULT_FLOCK_SETTINGS.turnSpeed,
        customMode: Boolean(flock?.customMode),
    };
}

const mapDispatchToProps = (dispatch) => {
    const updateElementsSettings = (properties) => dispatch(createActions.updateElementsSettings(properties));
    return { updateElementsSettings }
}

export default connect(mapStateToProps, mapDispatchToProps)(ParticlesFlockListItem);
